import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkle, CheckCircle, ForkKnife, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_STATE = {
  food_name: '',
  quantity: '',
  persons: '',
  order_date: '',
  order_time: '',
  delivery_address: '',
  full_name: '',
  phone: '',
  email: '',
  notes: '',
};

/**
 * CustomOrderModal — premium modal for off-menu custom order requests.
 * Props:
 *  - open (bool)
 *  - onClose ()
 */
const CustomOrderModal = ({ open, onClose }) => {
  const [form, setForm] = useState(INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  }, []);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleClose = () => {
    setForm(INITIAL_STATE);
    setConfirmation(null);
    onClose();
  };

  const validate = () => {
    const required = ['food_name', 'quantity', 'order_date', 'order_time', 'delivery_address', 'full_name', 'phone', 'email'];
    for (const f of required) {
      if (!form[f]?.trim()) {
        toast.error(`Please fill in ${f.replace(/_/g, ' ')}`);
        return false;
      }
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (!/^[0-9+\-\s]{10,15}$/.test(form.phone)) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/custom-order/submit`, form);
      setConfirmation(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border border-stone-200 focus:border-[#1A4331] focus:ring-4 focus:ring-[#1A4331]/10 rounded-2xl px-4 py-3 outline-none transition-all text-[15px] text-[#1C1917] placeholder:text-stone-400";
  const labelCls = "block text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-1.5";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={handleClose}
          data-testid="custom-order-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 26, stiffness: 260 }}
            className="relative w-full max-w-2xl bg-white rounded-[2rem] shadow-2xl overflow-hidden my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#1A4331] via-[#204d38] to-[#2A5A44] px-6 md:px-8 pt-8 pb-16 overflow-hidden">
              <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 bg-[#34D399]/25 rounded-full blur-3xl" />
              <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '18px 18px' }} />

              <button
                onClick={handleClose}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-all"
                data-testid="custom-order-close"
              >
                <X size={18} weight="bold" />
              </button>

              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/15 text-[#34D399] px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.14em] mb-4">
                  <Sparkle size={12} weight="fill" /> Off-Menu Request
                </div>
                <h2 className="text-2xl sm:text-3xl font-medium text-white font-heading tracking-tight mb-1.5">
                  Craving something custom?
                </h2>
                <p className="text-stone-200 text-sm max-w-md leading-relaxed">
                  Tell us what you want — our chefs will prepare it fresh. Pricing depends on quantity &amp; distance, so we&apos;ll confirm the quote within 24 hours.
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="relative -mt-8 px-4 md:px-6 pb-6">
              <div className="bg-white rounded-3xl border border-stone-100 shadow-xl shadow-stone-200/40 p-6 md:p-7">
                {confirmation ? (
                  <div className="text-center py-6" data-testid="custom-order-success">
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 14 }}
                      className="w-20 h-20 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center mx-auto mb-5 shadow-xl shadow-[#10B981]/30"
                    >
                      <CheckCircle size={40} weight="fill" className="text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-medium font-heading text-[#1C1917] mb-2 tracking-tight">Request received!</h3>
                    <p className="text-stone-600 text-sm mb-5 max-w-sm mx-auto leading-relaxed">
                      Our team will review your request and reach out within 24 hours with pricing &amp; delivery details.
                    </p>
                    <div className="bg-stone-50 border border-stone-100 rounded-2xl px-5 py-4 mb-6 inline-block">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-1">Your Request ID</p>
                      <p className="text-xl font-bold text-[#1A4331] font-heading tracking-tight" data-testid="custom-order-request-id">
                        {confirmation.request_id}
                      </p>
                    </div>
                    <div>
                      <button
                        onClick={handleClose}
                        className="bg-[#1A4331] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#2A5A44] transition-all shadow-lg shadow-[#1A4331]/20"
                        data-testid="custom-order-done-btn"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className={labelCls}>What would you like? *</label>
                      <input
                        type="text"
                        value={form.food_name}
                        onChange={handleChange('food_name')}
                        placeholder="e.g., Hyderabadi Chicken Biryani"
                        className={inputCls}
                        required
                        data-testid="custom-food-name"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Quantity *</label>
                        <input
                          type="text"
                          value={form.quantity}
                          onChange={handleChange('quantity')}
                          placeholder="e.g., 5 kg / 20 plates"
                          className={inputCls}
                          required
                          data-testid="custom-quantity"
                        />
                      </div>
                      <div>
                        <label className={labelCls}>No. of Persons</label>
                        <input
                          type="text"
                          value={form.persons}
                          onChange={handleChange('persons')}
                          placeholder="e.g., 20"
                          className={inputCls}
                          data-testid="custom-persons"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Date *</label>
                        <input
                          type="date"
                          value={form.order_date}
                          onChange={handleChange('order_date')}
                          min={minDate}
                          className={inputCls}
                          required
                          data-testid="custom-order-date"
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Time *</label>
                        <input
                          type="time"
                          value={form.order_time}
                          onChange={handleChange('order_time')}
                          className={inputCls}
                          required
                          data-testid="custom-order-time"
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>Delivery Address *</label>
                      <input
                        type="text"
                        value={form.delivery_address}
                        onChange={handleChange('delivery_address')}
                        placeholder="House/Flat, Street, City, Pincode"
                        className={inputCls}
                        required
                        data-testid="custom-address"
                      />
                    </div>

                    <div className="pt-2 border-t border-stone-100">
                      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-3">Your Contact</p>
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={form.full_name}
                          onChange={handleChange('full_name')}
                          placeholder="Full Name *"
                          className={inputCls}
                          required
                          data-testid="custom-full-name"
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input
                            type="tel"
                            value={form.phone}
                            onChange={handleChange('phone')}
                            placeholder="Phone *"
                            className={inputCls}
                            required
                            data-testid="custom-phone"
                          />
                          <input
                            type="email"
                            value={form.email}
                            onChange={handleChange('email')}
                            placeholder="Email *"
                            className={inputCls}
                            required
                            data-testid="custom-email"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>Notes <span className="text-stone-400 font-normal normal-case tracking-normal">(Optional)</span></label>
                      <textarea
                        value={form.notes}
                        onChange={handleChange('notes')}
                        rows={2}
                        placeholder="Dietary needs, spice level, allergies, etc."
                        className={`${inputCls} resize-none`}
                        data-testid="custom-notes"
                      />
                    </div>

                    <div className="flex items-start gap-2.5 bg-[#FEF3C7] border border-[#FCD34D]/60 text-[#92400E] px-4 py-2.5 rounded-2xl text-xs">
                      <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
                      <p><span className="font-semibold">No pricing on this page.</span> Cost depends on quantity, ingredients &amp; distance — we&apos;ll confirm your quote within 24 hours.</p>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white px-6 py-3.5 rounded-full font-semibold hover:shadow-2xl hover:shadow-[#1A4331]/30 hover:-translate-y-0.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none flex items-center justify-center gap-2 text-[15px]"
                      data-testid="custom-order-submit"
                    >
                      {submitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <ForkKnife size={16} weight="duotone" /> Submit Custom Request
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CustomOrderModal;
